import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { I18nService } from 'i18n';
import { CommunityMessagesService } from '../../../services/community-messages.service';
import { CommunityUnreadService } from '../../../services/community-unread.service';
import { COMMUNITY_MESSAGE_VIEWS, CommunityMessage, CommunityMessageView } from '../../../models/community-messages.models';
import { communityErrorKey } from '../../../models/community.models';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';

/**
 * Private messages inbox (`community/messages`, Phase 3): inbox, sent and
 * archive, each paginated. The unread count next to "Messages" in the shell
 * is `CommunityUnreadService`'s; opening a message (on the detail page) marks
 * it read there, so this list only refreshes the badge after an archive
 * action, which can also silently drop an unread message out of the inbox.
 */
@Component({
  selector: 'app-community-messages-page',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoModule, CommunityAvatarComponent],
  templateUrl: './community-messages-page.component.html',
  styleUrl: './community-messages-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityMessagesPageComponent implements OnInit, OnDestroy {
  private messagesService = inject(CommunityMessagesService);
  private unread = inject(CommunityUnreadService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  readonly views = COMMUNITY_MESSAGE_VIEWS;

  view = signal<CommunityMessageView>('inbox');
  messages = signal<CommunityMessage[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  busy = signal(false);

  page = signal(1);
  totalPages = signal(1);
  total = signal(0);

  private langSub?: Subscription;

  ngOnInit(): void {
    const requested = this.route.snapshot.queryParamMap.get('view') as CommunityMessageView | null;
    if (requested && this.views.includes(requested)) this.view.set(requested);
    this.unread.start();
    this.load(1);
    // Dates render in the UI language.
    this.langSub = this.i18n.currentLanguage$.subscribe(() => this.load(this.page()));
  }

  ngOnDestroy(): void {
    this.unread.stop();
    this.langSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  composeLink(): string {
    return `/${this.lang}/community/messages/new`;
  }

  detailLink(id: number): string {
    return `/${this.lang}/community/messages/${id}`;
  }

  selectView(view: CommunityMessageView): void {
    if (view === this.view()) return;
    this.view.set(view);
    this.router.navigate([], { relativeTo: this.route, queryParams: { view }, queryParamsHandling: 'merge', replaceUrl: true });
    this.load(1);
  }

  load(page: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.messagesService.list(this.view(), page).subscribe({
      next: res => {
        this.messages.set(res.data);
        this.page.set(res.page);
        this.totalPages.set(res.total_pages);
        this.total.set(res.total_count);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }

  /** The other party of a message, from the viewer's perspective (view decides which side that is). */
  counterpart(message: CommunityMessage) {
    return this.view() === 'sent' ? message.recipient : message.sender;
  }

  toggleArchive(message: CommunityMessage, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.busy.set(true);
    this.messagesService.archive(message.id, this.view() !== 'archive').subscribe({
      next: () => {
        this.busy.set(false);
        this.load(this.page());
        this.unread.refresh();
      },
      error: err => {
        this.busy.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }
}
